import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useSelector } from "react-redux";

import type { CampaignBuilderMode } from "../../../components/redesign/modules/CampaignBuilderForm";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { buildTargetingSummaryRows } from "../../../utils/buildTargetingSummaryRows";
import { getCampaignPlayTypeLabel } from "../../../constants/campaignPlayTypes";

const SummaryCard = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  borderRadius: 10,
  overflow: "hidden",
}));

const SummaryGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px 24px",
});

const TargetingStack = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const SectionTitle = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.brand.gray900,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  marginTop: 20,
  marginBottom: 12,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.brand.gray200}`,
}));

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const FieldValue = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 400,
  color: theme.palette.brand.gray900,
  wordBreak: "break-word" as const,
  whiteSpace: "pre-line" as const,
}));

const EMPTY = "—";

const formatCurrency = (value: number | string | undefined): string => {
  const n = Number(value);
  if (!value || Number.isNaN(n)) return EMPTY;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const formatDate = (value: string | undefined): string => {
  if (!value) return EMPTY;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

type Props = {
  mode?: CampaignBuilderMode;
};

const CampaignTemplateSummaryPanel: FC<Props> = ({ mode = "template" }) => {
  const campaignToCreate = useSelector((s: ReduxApplicationState) => s.campaignToCreate);
  const { selectedAdvertiser, campaignTemplateName, basicDetails, landingPage, campaignPlayType } = campaignToCreate;

  const rows: Array<{ label: string; value: string }> = [
    { label: "Advertiser Name", value: selectedAdvertiser?.name || EMPTY },
    ...(mode !== "campaign" ? [{ label: "Template Name", value: campaignTemplateName || EMPTY }] : []),
    { label: "Campaign Name", value: basicDetails.name || EMPTY },
    { label: "Campaign Type", value: getCampaignPlayTypeLabel(campaignPlayType) },
    { label: "Media Type", value: basicDetails.isVideoCampaign ? "Video" : "Display" },
    { label: "Start Date", value: formatDate(basicDetails.startDate as string) },
    { label: "End Date", value: formatDate(basicDetails.endDate as string) },
    { label: "Daily Budget", value: formatCurrency(basicDetails.dailyBudget) },
    { label: "Estimated Budget", value: formatCurrency(basicDetails.estimatedBudget) },
    { label: "Max Bid", value: formatCurrency(basicDetails.maxBid) },
    { label: "Landing Page URL", value: landingPage || EMPTY },
  ];

  const targetingRows = buildTargetingSummaryRows(campaignToCreate);

  return (
    <SummaryCard>
      <SummaryGrid>
        {rows.map((row) => (
          <FieldGroup key={row.label}>
            <FieldLabel>{row.label}</FieldLabel>
            <FieldValue>{row.value}</FieldValue>
          </FieldGroup>
        ))}
      </SummaryGrid>

      {targetingRows.length > 0 && (
        <>
          <SectionTitle>Targeting Settings</SectionTitle>
          <TargetingStack>
            {targetingRows.map((row) => (
              <FieldGroup key={row.label}>
                <FieldLabel>{row.label}</FieldLabel>
                <FieldValue>{row.value}</FieldValue>
              </FieldGroup>
            ))}
          </TargetingStack>
        </>
      )}
    </SummaryCard>
  );
};

export default CampaignTemplateSummaryPanel;
